import { useEffect, useState } from 'react';
import SchoolService from '../services/School';
import { createPagedMeta } from '../entities/PagedMeta';
import { createOptionsPage } from '../entities/OptionsPaged';
import isAdmin from '../helpers/isAdmin';
import resolveChildcareAccess from '../helpers/childcareAccess';

const storeKeySchoolChildcare = 'x-school-childcare';

const readStoredChildcare = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(storeKeySchoolChildcare) || '{}') || {};
  } catch (e: any) {
    return {};
  }
};

export default function useSchoolChildcare(token: string, user: any, appVersion: string, schoolId: string) {
  const [childcareBySchool, setChildcareBySchool] = useState<Record<string, boolean>>(readStoredChildcare);
  const isAdminUser = isAdmin(user);
  const childcareDeployed = appVersion === 'ALL';

  useEffect(() => {
    if (!token || !schoolId || isAdminUser || !childcareDeployed) {
      return;
    }

    let active = true;
    const refresh = async () => {
      let enabled: boolean | undefined;
      try {
        const result = await SchoolService(token).list({
          filters: [{ field: 'id', operation: 'hash', value: schoolId }],
          options: createOptionsPage(createPagedMeta()),
        });
        enabled = !!result?.data?.find((s: any) => s?.id === schoolId)?.childcareEnabled;
      } catch (e: any) {
        console.error(e?.response?.data?.message || 'Could not check childcare for this school');
      }
      if (!active) {
        return;
      }
      setChildcareBySchool((previous) => {
        const known = enabled ?? previous[schoolId] ?? false;
        const next = { ...previous, [schoolId]: known };
        localStorage.setItem(storeKeySchoolChildcare, JSON.stringify(next));
        return next;
      });
    };
    refresh();

    return () => {
      active = false;
    };
  }, [token, schoolId, isAdminUser, childcareDeployed]);

  return resolveChildcareAccess(appVersion, isAdminUser, schoolId ? childcareBySchool[schoolId] : undefined);
}
